import React, { useCallback, useMemo } from 'react';

import { useDispatch, useSelector, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button, Icon } from '@picsio/ui';
import { ShopifyColorful } from '@picsio/icons';

import { syncProductToDraft } from '../../../store/products/syncProductToDraft';
import { syncSelectedVariantsToDraft } from '../../../store/products/syncSelectedVariantsToDraft';
import { syncSingleVariantToDraft } from '../../../store/products/syncSingleVariantToDraft';
import { createAndPublishListingFromMaster } from '../../../store/products/createAndPublishListingFromMaster';
import { fetchAssets } from '../../../store/products/fetchAssets';
import { fetchVariantAssets } from '../../../store/products/fetchVariantAssets';
import { fetchListingAssets } from '../../../store/products/fetchListingAssets';
import {
  selectChannelOptions,
  selectProductListings,
  selectProductIsBusy,
  selectProductBusyLoadingText,
  selectMasterProduct,
  hasListingProductInMap,
  getListingProductIdFromMap,
} from '../../../store/selectors/productSelectors';
import { getActiveProduct } from '../../../store/products/selectors';
import localization from '../../../shared/strings';
import Logger from '../../../services/Logger';
import {
  hasActiveConnectedShopifyStore,
  hasConnectedShopifyStore,
  isSalesChannelDisconnected,
  SHOPIFY_CHANNEL_TYPE,
} from '../../teammates/views/Channels/helpers/salesChannelState';
import Tooltip from '../../Tooltip/Tooltip';
import { showVariantsSelectionDialog } from '../VariantsSelectionDialog';
import {
  getVariantChannelEntry,
  productHasAnyLiveVariant,
} from '../../../store/products/helpers/listingSyncHelpers';
import SYNC_SCENARIO from './constants/syncFlowConstants';
import ButtonLoader from './ButtonLoader';
import {
  getChannelsWithDraft,
  getChannelsWithLive,
  getSyncScenario,
} from './helpers/syncFlowHelpers';
import startSyncFlow from './helpers/startSyncFlow';
import { validatePublishUnmappedFieldsGuard } from '../helpers/runPublishUnmappedFieldsGuard';
import {
  resolveSyncChannelIds,
  splitChannelsByAction,
} from './helpers/syncFlowOrchestrator';
import {
  getProductMediaAssets,
  getMasterVariantAssets,
} from './helpers/syncFlowMediaHelpers';

const hasLoadedProductAssets = (productsState, product, productId) => (
  getProductMediaAssets(product, productsState, productId).length > 0
);

const hasLoadedVariantAssets = (productsState, product, variantId) => (
  getMasterVariantAssets(product, variantId, productsState).length > 0
);

const hasLoadedListingAssets = (productsState, listingProductId) => (
  Boolean(listingProductId)
  && productsState?.entities?.assets?.listingAssets?.[listingProductId] !== undefined
);

const hasLoadedListingVariantAssets = (productsState, listingVariantId) => (
  Boolean(listingVariantId)
  && productsState?.entities?.assets?.listingVariantAssets?.[listingVariantId] !== undefined
);

const logSyncCancelled = (actionName) => Logger.log('User', actionName || 'CreatedStageVersionCancelled');

const executeSyncFromFlowState = ({
  flowState,
  createDraft,
  publishDirectly,
}) => {
  const {
    channelActionsByChannelId,
    masterVariantIds,
    isVariantSync,
    overrideExisting,
    publishingScope,
    productMediaByChannel,
    variantMediaByChannel,
    variantMediaStepCompleted,
  } = flowState;

  const draftMedia = {
    publishingScope,
    productMediaByChannel,
    variantMediaByChannel,
    isVariantSync,
    variantMediaStepCompleted,
  };

  const { draftChannels, publishChannels } = splitChannelsByAction(channelActionsByChannelId);

  if (draftChannels.length) {
    createDraft({
      channelIds: draftChannels,
      masterVariantIds,
      isSingleVariant: isVariantSync,
      overrideExisting,
      draftMedia,
    });
  }

  if (publishChannels.length) {
    publishDirectly({
      channelIds: publishChannels,
      masterVariantIds,
      overrideExisting,
      draftMedia,
    });
  }
};

const ConnectChannelSection = ({ productId }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const location = useLocation();
  const { variantId } = getActiveProduct(location);
  const channelOptions = useSelector((state) => selectChannelOptions(state, productId));
  const product = useSelector((state) => selectMasterProduct(state, productId));
  const productListings = useSelector((state) => selectProductListings(state, productId));
  const team = useSelector((state) => state.user.team);
  const teamSalesChannels = useMemo(() => team?.salesChannels || [], [team?.salesChannels]);
  const isBusy = useSelector((state) => selectProductIsBusy(state, productId));
  const busyLoadingText = useSelector((state) => selectProductBusyLoadingText(state, productId));
  const connectedChannelOptions = channelOptions.filter(({ type }) => type !== 'master');
  const hasConnectedChannels = teamSalesChannels.length > 0;
  const isChannelDisconnected = hasConnectedShopifyStore(team) && !hasActiveConnectedShopifyStore(team);
  const isSyncDisabled = isBusy || isChannelDisconnected;
  const connectionLostTooltip = isChannelDisconnected
    ? localization.PRODUCT_INFO.publishedLayerConnectionLostTooltip
    : null;
  const text = hasConnectedChannels
    ? localization.PRODUCT_INFO.PREPARE_CHANNEL_VERSION
    : localization.PRODUCT_INFO.TO_PUBLISH_A_PRODUCT_TEXT;

  const teamChannelOptions = teamSalesChannels.map((salesChannel) => ({
    label: salesChannel.name,
    value: String(salesChannel._id),
    type: salesChannel.type,
    disconnected: isSalesChannelDisconnected(salesChannel),
    // eslint-disable-next-line jsx-control-statements/jsx-use-if-tag
    icon: salesChannel.type === SHOPIFY_CHANNEL_TYPE ? <ShopifyColorful /> : null,
  }));

  const disconnectedByChannelId = useMemo(
    () => new Map(
      teamSalesChannels.map((salesChannel) => [
        String(salesChannel._id),
        isSalesChannelDisconnected(salesChannel),
      ]),
    ),
    [teamSalesChannels],
  );

  const availableChannelOptions = Array.from(
    new Map(
      [
        ...connectedChannelOptions,
        ...teamChannelOptions,
      ].map((option) => [option.value, option]),
    ).values(),
  ).map((option) => ({
    ...option,
    disconnected: Boolean(
      option.disconnected ?? disconnectedByChannelId.get(String(option.value)),
    ),
  }));

  const createDraft = useCallback(({
    channelIds,
    masterVariantIds,
    isSingleVariant,
    draftMedia,
    overrideExisting,
  }) => {
    if (isSingleVariant && masterVariantIds?.length === 1) {
      dispatch(syncSingleVariantToDraft({
        productId,
        channelIds,
        masterVariantId: masterVariantIds[0],
        draftMedia,
        overrideExisting,
      }));
    } else if (masterVariantIds?.length) {
      dispatch(syncSelectedVariantsToDraft({
        productId,
        channelIds,
        masterVariantIds,
        draftMedia,
        overrideExisting,
      }));
    } else {
      dispatch(syncProductToDraft({
        productId,
        channelIds,
        draftMedia,
        overrideExisting,
      }));
    }

    Logger.log('User', 'CreatedStageVersionConfirm', { channelIds });
  }, [dispatch, productId]);

  const publishDirectly = useCallback(({
    channelIds,
    masterVariantIds,
    draftMedia,
    overrideExisting,
  }) => {
    dispatch(createAndPublishListingFromMaster({
      productId,
      channelIds,
      masterVariantIds,
      draftMedia,
      overrideExisting,
    }));
    Logger.log('User', 'PublishDirectlyConfirm', { channelIds });
  }, [dispatch, productId]);

  const handleCreateDraft = useCallback((
    selectedChannelIds,
    masterVariantIds,
    isSingleVariant = false,
    { overrideExisting = false, draftMedia = {} } = {},
  ) => {
    const createDrafts = (channelIds) => {
      if (!channelIds.length) {
        return;
      }

      createDraft({
        channelIds,
        masterVariantIds,
        isSingleVariant,
        draftMedia,
        overrideExisting,
      });
    };

    const channelsWithDraft = getChannelsWithDraft({
      selectedChannelIds,
      product,
      productsState: store.getState().products,
      productListings,
      masterVariantIds,
    });

    if (channelsWithDraft.length > 0) {
      Logger.log('User', 'CreateStageVersionAlreadyListed', { channelIds: channelsWithDraft });
    }

    const channelIdsToSync = resolveSyncChannelIds({
      selectedChannelIds,
      conflictingChannelIds: channelsWithDraft,
      overrideExisting,
    });

    createDrafts(channelIdsToSync);
  }, [createDraft, product, productListings, store]);

  const handlePublishDirectly = useCallback((
    selectedChannelIds,
    masterVariantIds,
    { overrideExisting = false, draftMedia = {} } = {},
  ) => {
    const publishLive = (channelIds) => {
      if (!channelIds.length) {
        return;
      }

      publishDirectly({
        channelIds,
        masterVariantIds,
        draftMedia,
        overrideExisting,
      });
    };

    const channelsWithLive = getChannelsWithLive({
      selectedChannelIds,
      product,
      productsState: store.getState().products,
      productListings,
      masterVariantIds,
    });

    if (channelsWithLive.length > 0) {
      Logger.log('User', 'PublishDirectlyAlreadyLive', { channelIds: channelsWithLive });
    }

    const channelIdsToSync = resolveSyncChannelIds({
      selectedChannelIds,
      conflictingChannelIds: channelsWithLive,
      overrideExisting,
    });

    publishLive(channelIdsToSync);
  }, [product, productListings, publishDirectly, store]);

  const shouldShowVariantNote = useCallback(
    (channelId) => Boolean(variantId && !hasListingProductInMap(productListings, channelId)),
    [productListings, variantId],
  );

  const openSyncDialog = useCallback(async ({ masterVariantIds, isVariant = false }) => {
    let productsState = store.getState().products;

    if (!hasLoadedProductAssets(productsState, product, productId) && !product.fetchingAssets) {
      try {
        await dispatch(fetchAssets({ productId })).unwrap();
      } catch {
        // fetchAssets already logs and toasts on failure
      }
      productsState = store.getState().products;
    }

    const variantIdsToPrefetch = masterVariantIds?.length
      ? masterVariantIds
      : (product.variants || []).map(({ _id }) => _id);

    const variantAssetFetches = variantIdsToPrefetch
      .filter((variantId) => !hasLoadedVariantAssets(productsState, product, variantId))
      .map((variantId) => dispatch(fetchVariantAssets({ variantId, productId })).unwrap().catch(() => {}));

    if (variantAssetFetches.length) {
      await Promise.all(variantAssetFetches);
      productsState = store.getState().products;
    }

    const listingAssetFetches = Object.keys(productListings || {})
      .map((channelId) => {
        const listingProductId = getListingProductIdFromMap(productListings, channelId);
        if (!listingProductId || hasLoadedListingAssets(productsState, listingProductId)) {
          return null;
        }

        return dispatch(fetchListingAssets({
          masterProductId: productId,
          channelId,
          listingProductId,
        })).unwrap().catch(() => {});
      })
      .filter(Boolean);

    if (listingAssetFetches.length) {
      await Promise.all(listingAssetFetches);
      productsState = store.getState().products;
    }

    const listingVariantAssetFetches = Object.keys(productListings || {})
      .flatMap((channelId) => variantIdsToPrefetch.map((masterVariantId) => {
        const listingVariant = getVariantChannelEntry(productsState, masterVariantId, channelId)
          || getVariantChannelEntry(product, masterVariantId, channelId);
        const listingVariantId = listingVariant?._id;
        if (!listingVariantId || hasLoadedListingVariantAssets(productsState, listingVariantId)) {
          return null;
        }

        return dispatch(fetchVariantAssets({
          variantId: listingVariantId,
          productId,
          channelId,
          masterVariantId,
        })).unwrap().catch(() => {});
      }))
      .filter(Boolean);

    if (listingVariantAssetFetches.length) {
      await Promise.all(listingVariantAssetFetches);
      productsState = store.getState().products;
    }

    startSyncFlow({
      product,
      productId,
      productsState,
      productListings,
      channelOptions: availableChannelOptions,
      masterVariantIds,
      isVariantSync: isVariant,
      shouldShowVariantNote,
      onCancel: logSyncCancelled,
      validateUnmappedFields: ({ channelIds, onUnmappedDialogOpen }) => validatePublishUnmappedFieldsGuard({
        channelIds,
        dispatch,
        salesChannels: teamSalesChannels,
        onUnmappedDialogOpen,
        onAbort: logSyncCancelled,
      }),
      onComplete: (flowState) => {
        executeSyncFromFlowState({
          flowState,
          createDraft: ({
            channelIds,
            masterVariantIds: variantIds,
            isSingleVariant,
            overrideExisting,
            draftMedia,
          }) => {
            handleCreateDraft(channelIds, variantIds, isSingleVariant, {
              overrideExisting,
              draftMedia,
            });
          },
          publishDirectly: ({
            channelIds,
            masterVariantIds: variantIds,
            overrideExisting,
            draftMedia,
          }) => {
            handlePublishDirectly(channelIds, variantIds, {
              overrideExisting,
              draftMedia,
            });
          },
        });
      },
    });
  }, [
    availableChannelOptions,
    dispatch,
    handleCreateDraft,
    handlePublishDirectly,
    product,
    productId,
    productListings,
    shouldShowVariantNote,
    store,
    teamSalesChannels,
  ]);

  const startSingleVariantSync = useCallback(() => {
    openSyncDialog({ masterVariantIds: [variantId], isVariant: true });
  }, [openSyncDialog, variantId]);

  const startChooseVariantsSync = useCallback(() => {
    const hasLiveVariant = productHasAnyLiveVariant(product, productListings);
    const dialogStrings = hasLiveVariant
      ? localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL.UPDATE_EXISTING
      : localization.DIALOGS.PRODUCT_DIALOGS.CONNECT_CHANNEL;

    showVariantsSelectionDialog({
      productTitle: product.title || '',
      variants: product.variants || [],
      strings: dialogStrings,
      variantsOptional: hasLiveVariant,
      onConfirm: ({ variantIds, isProductSelected }) => {
        const masterVariantIds = isProductSelected && !variantIds?.length ? [] : variantIds;
        openSyncDialog({ masterVariantIds });
      },
      onCancel: logSyncCancelled,
    });
  }, [openSyncDialog, product, productListings]);

  const startProductSync = useCallback(() => {
    openSyncDialog({});
  }, [openSyncDialog]);

  const runSyncScenario = useCallback((scenario) => {
    if (scenario === SYNC_SCENARIO.SINGLE_VARIANT) {
      startSingleVariantSync();
      return;
    }

    if (scenario === SYNC_SCENARIO.CHOOSE_VARIANTS) {
      startChooseVariantsSync();
      return;
    }

    startProductSync();
  }, [startChooseVariantsSync, startProductSync, startSingleVariantSync]);

  const onSyncButtonClick = useCallback(() => {
    const scenario = getSyncScenario({ variantId, product });

    runSyncScenario(scenario);
  }, [product, runSyncScenario, variantId]);

  return (
    <div className="d-flex ai-center jc-sb p-t-5 p-b-5">
      <p className="f-s-14 f-w-4 m-b-16 labelInputText">{text}</p>
      <If condition={hasConnectedChannels}>
        <Tooltip content={connectionLostTooltip}>
          <span>
            <Button variant="text" color="primary" disabled={isSyncDisabled} onClick={onSyncButtonClick}>
              <Choose>
                <When condition={isBusy}>
                  <ButtonLoader />
                </When>
                <Otherwise>
                  <Icon size="lg">{localization.PRODUCT_INFO.ICON}</Icon>
                </Otherwise>
              </Choose>
              <span className="m-l-5">{isBusy ? busyLoadingText : localization.PRODUCT_INFO.SYNC_TO_CHANNEL}</span>
            </Button>
          </span>
        </Tooltip>
      </If>
    </div>
  );
};

ConnectChannelSection.propTypes = {
  productId: PropTypes.string.isRequired,
};

export default ConnectChannelSection;
